import { useEffect, useRef } from "react";

/**
 * Requests a Screen Wake Lock ("screen") whenever `active` is true so
 * the phone screen doesn't sleep during an active voice call or
 * broadcast. Released automatically when `active` flips to false or
 * the component unmounts.
 *
 * The API is Chrome/Android + Safari 16.4+. Older browsers just no-op.
 *
 * We also re-acquire the lock when the tab becomes visible again —
 * the OS revokes wake locks on tab-hide, so the second the user
 * returns to the tab we need to ask for it back or their next
 * screen-timeout will kill the call.
 */
export default function useWakeLock(active) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) {
      return undefined;
    }
    let cancelled = false;

    const acquire = async () => {
      try {
        const s = await navigator.wakeLock.request("screen");
        if (cancelled) {
          try { await s.release(); } catch (_) { /* ignore */ }
          return;
        }
        sentinelRef.current = s;
        // If the OS drops the lock (background, low battery), clear
        // our reference so a re-acquire on visibilitychange works.
        s.addEventListener("release", () => { sentinelRef.current = null; });
      } catch (_) {
        // Permission denied, feature not usable — safe to ignore.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !sentinelRef.current) {
        acquire();
      }
    };

    acquire();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      const s = sentinelRef.current;
      sentinelRef.current = null;
      if (s) {
        try { s.release(); } catch (_) { /* ignore */ }
      }
    };
  }, [active]);
}
